import React, { useState, useEffect, useMemo, useRef, UIEvent } from 'react';
import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import VirtualisedGrid, { SelectionRange, parseCellId, colNameToIndex, getColName } from './VirtualisedGrid';
import { FormulaBar } from './FormulaBar';
import { ConditionalFormatModal } from './ConditionalFormatModal';
import { FindReplaceModal } from './FindReplaceModal';
import { CustomCurrencyModal } from './CustomCurrencyModal';
import { FilterModal } from './FilterModal';
import { SplitTextModal } from './SplitTextModal';
import { FormulaListModal } from './FormulaListModal'; 
import { FeedbackModal } from './FeedbackModal';
import { ConfirmModal } from './ConfirmModal';
import { useComputeWorker } from '../hooks/useComputeWorker';
import { toCoord } from '../engine/ast.utils';
import { TOTAL_COLS, TOTAL_ROWS } from '../constants';
import { documentService } from '../services/document.service';
import { useAuth } from '../context/AuthContext';
import { usePWAInstall } from '../hooks/usePWAInstall';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface WorkspaceProps {
    ydoc: Y.Doc;
    awareness: Awareness;
    initialImport?: { importBase64: string, fileName: string };
    onOpenShare: () => void;
    isReadOnlyUser: boolean;
}

const FONTS = [
    'Arial', 'Courier New', 'Georgia', 'Impact', 'Lucida Console', 'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana'
];

export const SpreadsheetWorkspace: React.FC<WorkspaceProps> = ({ ydoc, awareness, initialImport, onOpenShare, isReadOnlyUser }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { token, user } = useAuth();
    const { isInstallable, promptInstall } = usePWAInstall();

    const [activeCellId, setActiveCellId] = useState<string | null>(null);
    const [selection, setSelection] = useState<SelectionRange | null>(null);
    const [activeSheetId, setActiveSheetId] = useState<string>('cells');
    const [editingSheet, setEditingSheet] = useState<string | null>(null);
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    
    // View States
    const [zoom, setZoom] = useState<number>(1);
    const [freezeRow, setFreezeRow] = useState<number>(0);
    const [freezeCol, setFreezeCol] = useState<number>(0);
    const [groupedRows, setGroupedRows] = useState<Set<number>>(new Set());
    const [groupedCols, setGroupedCols] = useState<Set<number>>(new Set());

    // Modals
    const [isConditionalModalOpen, setIsConditionalModalOpen] = useState(false);
    const [isFindReplaceModalOpen, setIsFindReplaceModalOpen] = useState(false);
    const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isSplitTextModalOpen, setIsSplitTextModalOpen] = useState(false);
    const [isFormulaListModalOpen, setIsFormulaListModalOpen] = useState(false); 
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    
    const [, setVersion] = useState(0);
    const [undoManager, setUndoManager] = useState<Y.UndoManager | null>(null);

    // Data Engine States
    const [filterRule, setFilterRule] = useState<{ col: number, condition: string } | null>(null);
    const [filteredHiddenRows, setFilteredHiddenRows] = useState<Set<number>>(new Set());

    // Import States
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importMode, setImportMode] = useState<'overwrite-doc' | 'insert-sheets' | 'replace-sheet'>('overwrite-doc');
    const [hasProcessedInitialImport, setHasProcessedInitialImport] = useState(false);

    const sheetOrder = ydoc.getArray<string>('sheetOrder');
    const sheetMeta = ydoc.getMap<string>('sheetMeta');
    const sheetSize = ydoc.getMap<number>('sheetSize');
    const conditionalRules = ydoc.getArray<any>('conditionalRules');
    const cellsMap = ydoc.getMap<string>(activeSheetId === 'cells' ? 'cells' : `cells-${activeSheetId}`);
    const formatsMap = ydoc.getMap<any>(activeSheetId === 'cells' ? 'formats' : `formats-${activeSheetId}`);
    const colWidthsMap = ydoc.getMap<number>(activeSheetId === 'cells' ? 'colWidths' : `colWidths-${activeSheetId}`);
    const rowHeightsMap = ydoc.getMap<number>(activeSheetId === 'cells' ? 'rowHeights' : `rowHeights-${activeSheetId}`);

    const { computedValues } = useComputeWorker(cellsMap, activeSheetId === 'cells' ? 'cells' : `cells-${activeSheetId}`); 
    const currentComputedValue = activeCellId ? computedValues[activeCellId] : null;

    const totalRows = sheetSize.get(`rows-${activeSheetId}`) || TOTAL_ROWS;
    const totalCols = sheetSize.get(`cols-${activeSheetId}`) || TOTAL_COLS;

    // Determine Quota limits and read-only state
    const maxStorage = (user?.tier === 'PRO' || user?.tier === 'TEAM') ? 500 * 1024 * 1024 : 50 * 1024 * 1024;
    const storageUsed = user?.storageUsed || 0;
    const isOverQuota = storageUsed >= maxStorage;
    const isReadOnly = isOverQuota || isReadOnlyUser;

    const processImport = (buffer: ArrayBuffer, mode: 'overwrite-doc' | 'insert-sheets' | 'replace-sheet') => {
        if (isReadOnly) return;
        try {
            const wb = XLSX.read(buffer, { type: 'array', cellFormula: true });
            
            ydoc.transact(() => {
                let targetSheetIds: string[] = [];

                if (mode === 'overwrite-doc') {
                    const currentSheets = sheetOrder.toArray();
                    sheetOrder.delete(0, currentSheets.length);
                    
                    wb.SheetNames.forEach((name, i) => {
                        const newId = i === 0 ? 'cells' : `sheet-${Date.now()}-${i}`;
                        sheetOrder.push([newId]);
                        sheetMeta.set(newId, name);
                        targetSheetIds.push(newId);
                    });
                    setActiveSheetId('cells');
                    conditionalRules.delete(0, conditionalRules.length);
                } else if (mode === 'insert-sheets') {
                    wb.SheetNames.forEach((name, i) => {
                        const newId = `sheet-${Date.now()}-${i}`;
                        sheetOrder.push([newId]);
                        sheetMeta.set(newId, name);
                        targetSheetIds.push(newId);
                    });
                } else if (mode === 'replace-sheet') {
                    targetSheetIds.push(activeSheetId);
                    sheetMeta.set(activeSheetId, wb.SheetNames[0]);
                    
                    const rules = conditionalRules.toArray();
                    for (let j = rules.length - 1; j >= 0; j--) {
                        if (rules[j].sheetId === activeSheetId) {
                            conditionalRules.delete(j, 1);
                        }
                    }
                }

                wb.SheetNames.forEach((name, i) => {
                    if (mode === 'replace-sheet' && i > 0) return;
                    
                    const sheetId = targetSheetIds[i];
                    const ws = wb.Sheets[name];
                    const mapName = sheetId === 'cells' ? 'cells' : `cells-${sheetId}`;
                    const cMap = ydoc.getMap<string>(mapName);
                    const fMap = ydoc.getMap<any>(mapName === 'cells' ? 'formats' : mapName.replace('cells-', 'formats-'));
                    const cwMap = ydoc.getMap<number>(mapName === 'cells' ? 'colWidths' : mapName.replace('cells-', 'colWidths-'));
                    const rhMap = ydoc.getMap<number>(mapName === 'cells' ? 'rowHeights' : mapName.replace('cells-', 'rowHeights-'));
                    
                    if (mode === 'overwrite-doc' || mode === 'replace-sheet') {
                        Array.from(cMap.keys()).forEach(k => cMap.delete(k));
                        Array.from(fMap.keys()).forEach(k => fMap.delete(k));
                        Array.from(cwMap.keys()).forEach(k => cwMap.delete(k));
                        Array.from(rhMap.keys()).forEach(k => rhMap.delete(k));
                    }

                    let maxR = 0;
                    let maxC = 0;

                    for (const key in ws) {
                        if (key.startsWith('!')) continue;
                        const cell = ws[key];
                        const val = cell.f ? `=${cell.f}` : cell.v;
                        if (val !== undefined && val !== null && val !== '') {
                            cMap.set(key, String(val));
                            const coords = parseCellId(key);
                            if (coords.r > maxR) maxR = coords.r;
                            if (coords.c > maxC) maxC = coords.c;
                        }
                    }

                    sheetSize.set(`rows-${sheetId}`, Math.max(maxR + 50, TOTAL_ROWS));
                    sheetSize.set(`cols-${sheetId}`, Math.max(maxC + 10, TOTAL_COLS));
                });
            });
        } catch (err) {
            console.error("Failed to parse/import file", err);
            alert("Failed to import file. Ensure it is a valid spreadsheet format.");
        }
    };

    useEffect(() => {
        if (initialImport && !hasProcessedInitialImport) {
            setHasProcessedInitialImport(true);
            try {
                const binaryString = atob(initialImport.importBase64);
                const len = binaryString.length;
                const bytes = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                processImport(bytes.buffer, 'overwrite-doc');
                ydoc.getMap<string>('metadata').set('title', initialImport.fileName.replace(/\.[^/.]+$/, ""));
            } catch (e) {
                console.error(e);
            }
        }
    }, [initialImport, hasProcessedInitialImport, ydoc]);

    useEffect(() => {
        if (sheetOrder.length === 0) {
            ydoc.transact(() => {
                sheetOrder.push(['cells']); 
                sheetMeta.set('cells', 'Sheet1');
            });
        }
        
        const handleUpdate = () => setVersion(v => v + 1);
        sheetOrder.observe(handleUpdate);
        sheetMeta.observe(handleUpdate);
        sheetSize.observe(handleUpdate);
        conditionalRules.observe(handleUpdate);
        colWidthsMap.observe(handleUpdate);
        rowHeightsMap.observe(handleUpdate);
        
        return () => {
            sheetOrder.unobserve(handleUpdate);
            sheetMeta.unobserve(handleUpdate);
            sheetSize.unobserve(handleUpdate);
            conditionalRules.unobserve(handleUpdate);
            colWidthsMap.unobserve(handleUpdate);
            rowHeightsMap.unobserve(handleUpdate);
        };
    }, [ydoc, sheetOrder, sheetMeta, sheetSize, conditionalRules, colWidthsMap, rowHeightsMap]);

    useEffect(() => {
        const um = new Y.UndoManager([cellsMap, formatsMap, conditionalRules, sheetSize, colWidthsMap, rowHeightsMap]);
        setUndoManager(um);
        return () => um.destroy();
    }, [cellsMap, formatsMap, conditionalRules, sheetSize, colWidthsMap, rowHeightsMap]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (isReadOnly) return;
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                undoManager?.undo();
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
                e.preventDefault();
                undoManager?.redo();
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
                const activeEl = document.activeElement as HTMLElement;
                const isInput = activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA';
                if (!isInput) {
                    e.preventDefault();
                    setSelection({ startR: 0, endR: totalRows - 1, startC: 0, endC: totalCols - 1 });
                }
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
                e.preventDefault();
                setIsFindReplaceModalOpen(true);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [undoManager, totalRows, totalCols, isReadOnly]);

    useEffect(() => {
        if (!filterRule) {
            setFilteredHiddenRows(new Set());
            return;
        }
        
        const checkCondition = (value: any, condition: string) => {
            if (!condition) return false;
            const numValue = Number(value);
            const strValue = String(value).toUpperCase();
            const upperCond = condition.toUpperCase();

            if (condition.startsWith('>=')) return !isNaN(numValue) && numValue >= Number(condition.slice(2));
            if (condition.startsWith('<=')) return !isNaN(numValue) && numValue <= Number(condition.slice(2));
            if (condition.startsWith('>')) return !isNaN(numValue) && numValue > Number(condition.slice(1));
            if (condition.startsWith('<')) return !isNaN(numValue) && numValue < Number(condition.slice(1));
            if (condition.startsWith('=')) return strValue === condition.slice(1).toUpperCase();
            return strValue === upperCond;
        };

        const newHidden = new Set<number>();
        let maxR = 0;
        for (const key of cellsMap.keys()) {
            const r = parseCellId(key).r;
            if (r > maxR) maxR = r;
        }

        for (let r = 0; r <= maxR; r++) {
            const cellId = toCoord(filterRule.col, r);
            const val = computedValues[cellId] ?? cellsMap.get(cellId) ?? '';
            if (!checkCondition(val, filterRule.condition)) {
                newHidden.add(r);
            }
        }
        setFilteredHiddenRows(newHidden);
    }, [filterRule, cellsMap, computedValues]);

    const effectiveHiddenRows = useMemo(() => {
        return new Set([...Array.from(filteredHiddenRows), ...Array.from(groupedRows)]);
    }, [filteredHiddenRows, groupedRows]);

    const handleMenuClick = (e: React.MouseEvent, menuName: string) => {
        e.stopPropagation();
        setActiveMenu(activeMenu === menuName ? null : menuName);
    };

    const handleMenuHover = (menuName: string) => {
        if (activeMenu && activeMenu !== menuName && !activeMenu.startsWith('Mobile')) {
            setActiveMenu(menuName);
        }
    };

    useEffect(() => {
        const handleClickOutside = () => setActiveMenu(null);
        if (activeMenu) document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [activeMenu]);

    const getTargetRow = () => activeCellId ? parseCellId(activeCellId).r : (selection ? Math.min(selection.startR, selection.endR) : null);
    const getTargetCol = () => activeCellId ? parseCellId(activeCellId).c : (selection ? Math.min(selection.startC, selection.endC) : null);

    const shiftCells = (dimension: 'row' | 'col', targetIndex: number, amount: number = 1) => {
        if (isReadOnly) return;
        ydoc.transact(() => {
            const shiftMapDown = (map: Y.Map<any>, isFormulas = false) => {
                const toSet: { newKey: string; val: any }[] = [];
                const keysToDelete = new Set<string>();
                for (const key of Array.from(map.keys())) {
                    const coords = parseCellId(key);
                    const shouldShift = dimension === 'row' ? coords.r >= targetIndex : coords.c >= targetIndex;
                    let val = map.get(key);
                    let newKey = key;

                    if (shouldShift) {
                        newKey = dimension === 'row' 
                            ? toCoord(coords.c, coords.r + amount)
                            : toCoord(coords.c + amount, coords.r);
                        keysToDelete.add(key);
                    }
                    if (isFormulas && typeof val === 'string' && val.startsWith('=')) {
                        const originalVal = val;
                        val = val.replace(/\b([A-Za-z]+)(\d+)\b/g, (match, colStr, rowStr) => {
                            let c = colNameToIndex(colStr.toUpperCase());
                            let r = parseInt(rowStr, 10) - 1;
                            if (dimension === 'row' && r >= targetIndex) r += amount;
                            else if (dimension === 'col' && c >= targetIndex) c += amount;
                            return `${getColName(c)}${r + 1}`;
                        });
                        if (!shouldShift && originalVal !== val) toSet.push({ newKey, val });
                    }
                    if (shouldShift) toSet.push({ newKey, val });
                }
                toSet.forEach(item => keysToDelete.delete(item.newKey));
                toSet.forEach(item => map.set(item.newKey, item.val));
                keysToDelete.forEach(k => map.delete(k));
            };

            const shiftDimensionMap = (map: Y.Map<number>, targetIndex: number, amount: number) => {
                const toSet: { newKey: string; val: number }[] = [];
                const keysToDelete = new Set<string>();
                for (const key of Array.from(map.keys())) {
                    const idx = parseInt(key, 10);
                    if (idx >= targetIndex) {
                        toSet.push({ newKey: (idx + amount).toString(), val: map.get(key)! });
                        keysToDelete.add(key);
                    }
                }
                toSet.forEach(item => keysToDelete.delete(item.newKey));
                toSet.forEach(item => map.set(item.newKey, item.val));
                keysToDelete.forEach(k => map.delete(k));
            };

            shiftMapDown(cellsMap, true);
            shiftMapDown(formatsMap, false);
            
            if (dimension === 'row') {
                shiftDimensionMap(rowHeightsMap, targetIndex, amount);
                sheetSize.set(`rows-${activeSheetId}`, totalRows + amount);
            } else {
                shiftDimensionMap(colWidthsMap, targetIndex, amount);
                sheetSize.set(`cols-${activeSheetId}`, totalCols + amount);
            }
        });
        setActiveMenu(null);
    };

    const handleDeleteOperation = (type: 'values' | 'formatting' | 'row' | 'col' | 'shiftUp' | 'shiftLeft') => {
        if (isReadOnly || (!activeCellId && !selection)) return;

        const startC = selection ? Math.min(selection.startC, selection.endC) : parseCellId(activeCellId!).c;
        const endC = selection ? Math.max(selection.startC, selection.endC) : parseCellId(activeCellId!).c;
        const startR = selection ? Math.min(selection.startR, selection.endR) : parseCellId(activeCellId!).r;
        const endR = selection ? Math.max(selection.startR, selection.endR) : parseCellId(activeCellId!).r;
        const rowCount = endR - startR + 1;
        const colCount = endC - startC + 1;

        ydoc.transact(() => {
            if (type === 'values') {
                for (const key of Array.from(cellsMap.keys())) {
                    const coords = parseCellId(key);
                    if (coords.r >= startR && coords.r <= endR && coords.c >= startC && coords.c <= endC) {
                        cellsMap.delete(key);
                    }
                }
            } else if (type === 'formatting') {
                for (const key of Array.from(formatsMap.keys())) {
                    const coords = parseCellId(key);
                    if (coords.r >= startR && coords.r <= endR && coords.c >= startC && coords.c <= endC) {
                        formatsMap.delete(key);
                    }
                }
            } else {
                const applyShiftMap = (map: Y.Map<any>, isFormulas = false) => {
                    const updates = new Map<string, any>();
                    const deletes = new Set<string>();

                    for (const key of Array.from(map.keys())) {
                        const coords = parseCellId(key);
                        let newKey = null;
                        let val = map.get(key);

                        if (type === 'row') {
                            if (coords.r >= startR && coords.r <= endR) deletes.add(key);
                            else if (coords.r > endR) newKey = toCoord(coords.c, coords.r - rowCount);
                        } else if (type === 'col') {
                            if (coords.c >= startC && coords.c <= endC) deletes.add(key);
                            else if (coords.c > endC) newKey = toCoord(coords.c - colCount, coords.r);
                        } else if (type === 'shiftUp') {
                            if (coords.c >= startC && coords.c <= endC) {
                                if (coords.r >= startR && coords.r <= endR) deletes.add(key);
                                else if (coords.r > endR) newKey = toCoord(coords.c, coords.r - rowCount);
                            }
                        } else if (type === 'shiftLeft') {
                            if (coords.r >= startR && coords.r <= endR) {
                                if (coords.c >= startC && coords.c <= endC) deletes.add(key);
                                else if (coords.c > endC) newKey = toCoord(coords.c - colCount, coords.r);
                            }
                        }

                        if (isFormulas && typeof val === 'string' && val.startsWith('=')) {
                            const originalVal = val;
                            val = val.replace(/\b([A-Za-z]+)(\d+)\b/g, (match, colStr, rowStr) => {
                                let c = colNameToIndex(colStr.toUpperCase());
                                let r = parseInt(rowStr, 10) - 1;
                                
                                if (type === 'row' || type === 'shiftUp') {
                                    if (r >= startR && r <= endR) return '#REF!';
                                    if (r > endR) r -= rowCount;
                                } else if (type === 'col' || type === 'shiftLeft') {
                                    if (c >= startC && c <= endC) return '#REF!';
                                    if (c > endC) c -= colCount;
                                }
                                return `${getColName(c)}${r + 1}`;
                            });
                            if (!newKey && originalVal !== val && !deletes.has(key)) updates.set(key, val);
                        }
                        if (newKey) {
                            updates.set(newKey, val);
                            deletes.add(key);
                        }
                    }
                    deletes.forEach(k => map.delete(k));
                    updates.forEach((v, k) => map.set(k, v));
                };

                const applyShiftDimMap = (map: Y.Map<number>, isRow: boolean) => {
                    if ((type === 'row' && !isRow) || (type === 'col' && isRow)) return;
                    const startIdx = isRow ? startR : startC;
                    const endIdx = isRow ? endR : endC;
                    const count = isRow ? rowCount : colCount;
                    
                    const updates = new Map<string, number>();
                    const deletes = new Set<string>();
                    for (const key of Array.from(map.keys())) {
                        const idx = parseInt(key, 10);
                        if (idx >= startIdx && idx <= endIdx) {
                            deletes.add(key);
                        } else if (idx > endIdx) {
                            updates.set((idx - count).toString(), map.get(key)!);
                            deletes.add(key);
                        }
                    }
                    deletes.forEach(k => map.delete(k));
                    updates.forEach((v, k) => map.set(k, v));
                };

                applyShiftMap(cellsMap, true);
                applyShiftMap(formatsMap, false);
                if (type === 'row' || type === 'col') {
                    applyShiftDimMap(rowHeightsMap, true);
                    applyShiftDimMap(colWidthsMap, false);
                }
            }
        });
        setActiveMenu(null);
    };

    const handleDeleteSheet = (targetSheetId: string) => {
        if (isReadOnly) return;
        const currentSheets = sheetOrder.toArray();
        if (currentSheets.length <= 1) {
            alert("You cannot delete the last sheet in a document.");
            return;
        }

        const sheetName = sheetMeta.get(targetSheetId) || 'this sheet';
        if (!window.confirm(`Are you sure you want to delete "${sheetName}"? This action cannot be undone.`)) return;

        ydoc.transact(() => {
            const index = currentSheets.indexOf(targetSheetId);
            if (index !== -1) sheetOrder.delete(index, 1);

            sheetMeta.delete(targetSheetId);
            sheetSize.delete(`rows-${targetSheetId}`);
            sheetSize.delete(`cols-${targetSheetId}`);

            const mapName = targetSheetId === 'cells' ? 'cells' : `cells-${targetSheetId}`;
            const fMapName = targetSheetId === 'cells' ? 'formats' : `formats-${targetSheetId}`;
            const cMap = ydoc.getMap<string>(mapName);
            const fMap = ydoc.getMap<any>(fMapName);
            const cwMap = ydoc.getMap<number>(targetSheetId === 'cells' ? 'colWidths' : `colWidths-${targetSheetId}`);
            const rhMap = ydoc.getMap<number>(targetSheetId === 'cells' ? 'rowHeights' : `rowHeights-${targetSheetId}`);
            
            Array.from(cMap.keys()).forEach(k => cMap.delete(k));
            Array.from(fMap.keys()).forEach(k => fMap.delete(k));
            Array.from(cwMap.keys()).forEach(k => cwMap.delete(k));
            Array.from(rhMap.keys()).forEach(k => rhMap.delete(k));

            const rules = conditionalRules.toArray();
            for (let j = rules.length - 1; j >= 0; j--) {
                if (rules[j].sheetId === targetSheetId) {
                    conditionalRules.delete(j, 1);
                }
            }
        });

        if (activeSheetId === targetSheetId) {
            const remaining = sheetOrder.toArray();
            setActiveSheetId(remaining[0]);
            setActiveCellId(null);
            setSelection(null);
        }
    };

    const handleGroup = (dimension: 'row' | 'col', isGrouping: boolean) => {
        if (!selection && !activeCellId) return;
        const start = dimension === 'row' 
            ? (selection ? Math.min(selection.startR, selection.endR) : parseCellId(activeCellId!).r)
            : (selection ? Math.min(selection.startC, selection.endC) : parseCellId(activeCellId!).c);
        const end = dimension === 'row'
            ? (selection ? Math.max(selection.startR, selection.endR) : parseCellId(activeCellId!).r)
            : (selection ? Math.max(selection.startC, selection.endC) : parseCellId(activeCellId!).c);
            
        const setMethod = dimension === 'row' ? setGroupedRows : setGroupedCols;
        setMethod(prev => {
            const next = new Set(prev);
            for(let i = start; i <= end; i++) {
                if (isGrouping) next.add(i);
                else next.delete(i);
            }
            return next;
        });
        setActiveMenu(null);
    };

    const handleUngroupRowBlock = (endRow: number) => {
        setGroupedRows(prev => {
            const next = new Set(prev);
            let r = endRow - 1;
            while(r >= 0 && next.has(r)) { next.delete(r); r--; }
            return next;
        });
    };

    const handleUngroupColBlock = (endCol: number) => {
        setGroupedCols(prev => {
            const next = new Set(prev);
            let c = endCol - 1;
            while(c >= 0 && next.has(c)) { next.delete(c); c--; }
            return next;
        });
    };

    const handleCopy = async (isCut = false) => {
        if (!selection && !activeCellId) return;
        if (isCut && isReadOnly) return; 

        const startC = selection ? Math.min(selection.startC, selection.endC) : parseCellId(activeCellId!).c;
        const endC = selection ? Math.max(selection.startC, selection.endC) : parseCellId(activeCellId!).c;
        const startR = selection ? Math.min(selection.startR, selection.endR) : parseCellId(activeCellId!).r;
        const endR = selection ? Math.max(selection.startR, selection.endR) : parseCellId(activeCellId!).r;

        let maxR = 0, maxC = 0;
        for (const key of cellsMap.keys()) {
            const coords = parseCellId(key);
            if (coords.r > maxR) maxR = coords.r;
            if (coords.c > maxC) maxC = coords.c;
        }
        const effectiveEndR = Math.min(endR, maxR);
        const effectiveEndC = Math.min(endC, maxC);

        let tsv = '';
        ydoc.transact(() => {
            for (let r = startR; r <= effectiveEndR; r++) {
                const rowData = [];
                for (let c = startC; c <= effectiveEndC; c++) {
                    const cellId = toCoord(c, r);
                    rowData.push(cellsMap.get(cellId) || '');
                    if (isCut) {
                        cellsMap.delete(cellId);
                        formatsMap.delete(cellId);
                    }
                }
                tsv += rowData.join('\t') + '\n';
            }
        });

        try {
            await navigator.clipboard.writeText(tsv);
        } catch (err) {
            console.error("Failed to write to clipboard", err);
        }
    };

    const handlePaste = async () => {
        if (isReadOnly || !activeCellId) return;
        try {
            const text = await navigator.clipboard.readText();
            const rows = text.split('\n').map(r => r.split('\t'));
            const startCoord = parseCellId(activeCellId);
            
            ydoc.transact(() => {
                rows.forEach((row, rOffset) => {
                    if (row.length === 1 && row[0] === '' && rOffset === rows.length - 1) return; 
                    row.forEach((val, cOffset) => {
                        const targetCellId = toCoord(startCoord.c + cOffset, startCoord.r + rOffset);
                        if (val !== '') {
                            cellsMap.set(targetCellId, val);
                        } else {
                            cellsMap.delete(targetCellId);
                        }
                    });
                });
            });
        } catch (err) {
            console.error('Failed to read clipboard', err);
        }
    };

    const triggerImport = (mode: 'overwrite-doc' | 'insert-sheets' | 'replace-sheet') => {
        setImportMode(mode);
        fileInputRef.current?.click();
        setActiveMenu(null);
    };

    const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const buffer = await file.arrayBuffer();
        processImport(buffer, importMode);
        e.target.value = ''; 
    };

    const handlePrint = () => {
        let maxR = 0, maxC = 0;
        for (const key of cellsMap.keys()) {
            const coords = parseCellId(key);
            if (coords.r > maxR) maxR = coords.r;
            if (coords.c > maxC) maxC = coords.c;
        }
        const data: any[][] = [];
        for (let r = 0; r <= maxR; r++) {
            const rowData: any[] = [];
            for (let c = 0; c <= maxC; c++) {
                const cellId = toCoord(c, r);
                rowData.push(computedValues[cellId] ?? cellsMap.get(cellId) ?? '');
            }
            data.push(rowData);
        }
        const sheetName = sheetMeta.get(activeSheetId) || 'Sheet1';
        const doc = new jsPDF('landscape');
        doc.text(sheetName, 14, 15);
        autoTable(doc, {
            startY: 20,
            body: data,
            styles: { fontSize: 8, cellPadding: 2 },
            theme: 'grid'
        });
        doc.autoPrint();
        window.open(doc.output('bloburl'), '_blank');
        setActiveMenu(null);
    };

    const confirmDeleteDocument = async () => {
        try {
            const documentId = window.location.pathname.split('/').pop();
            if (documentId && token) {
                await documentService.deleteDocument(token, documentId);
                navigate('/');
            }
        } catch (err) {
            console.error("Failed to delete document", err);
        }
    };

    const handleExport = (format: 'xlsx' | 'ods' | 'csv' | 'tsv' | 'pdf') => {
        let maxR = 0, maxC = 0;
        for (const key of cellsMap.keys()) {
            const coords = parseCellId(key);
            if (coords.r > maxR) maxR = coords.r;
            if (coords.c > maxC) maxC = coords.c;
        }
        const sheetName = sheetMeta.get(activeSheetId) || 'Sheet1';
        const fileName = `${sheetName}_Export`;

        if (format === 'csv' || format === 'tsv') {
            const data: any[][] = [];
            for (let r = 0; r <= maxR; r++) {
                const rowData: any[] = [];
                for (let c = 0; c <= maxC; c++) {
                    const cellId = toCoord(c, r);
                    rowData.push(computedValues[cellId] ?? cellsMap.get(cellId) ?? '');
                }
                data.push(rowData);
            }
            const delimiter = format === 'csv' ? ',' : '\t';
            const content = data.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(delimiter)).join('\n');
            const blob = new Blob([content], { type: format === 'csv' ? 'text/csv;charset=utf-8;' : 'text/tab-separated-values;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `${fileName}.${format}`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (format === 'xlsx' || format === 'ods') {
            const worksheet: XLSX.WorkSheet = {};
            const range = { s: { c: 0, r: 0 }, e: { c: maxC, r: maxR } };
            for (let r = 0; r <= maxR; r++) {
                for (let c = 0; c <= maxC; c++) {
                    const cellId = toCoord(c, r);
                    const rawVal = cellsMap.get(cellId) || '';
                    const computedVal = computedValues[cellId] ?? rawVal;
                    const cellRef = XLSX.utils.encode_cell({ c, r });

                    if (rawVal.startsWith('=')) {
                        worksheet[cellRef] = {
                            t: isNaN(Number(computedVal)) ? 's' : 'n',
                            v: isNaN(Number(computedVal)) ? computedVal : Number(computedVal),
                            f: rawVal.substring(1) 
                        };
                    } else if (rawVal !== '') {
                        worksheet[cellRef] = {
                            t: isNaN(Number(rawVal)) ? 's' : 'n',
                            v: isNaN(Number(rawVal)) ? rawVal : Number(rawVal)
                        };
                    }
                }
            }
            worksheet['!ref'] = XLSX.utils.encode_range(range);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31));
            XLSX.writeFile(workbook, `${fileName}.${format}`);
        } else if (format === 'pdf') {
            const data: any[][] = [];
            for (let r = 0; r <= maxR; r++) {
                const rowData: any[] = [];
                for (let c = 0; c <= maxC; c++) {
                    const cellId = toCoord(c, r);
                    rowData.push(computedValues[cellId] ?? cellsMap.get(cellId) ?? '');
                }
                data.push(rowData);
            }
            const doc = new jsPDF('landscape');
            doc.text(fileName, 14, 15);
            autoTable(doc, {
                startY: 20,
                body: data,
                styles: { fontSize: 8, cellPadding: 2 },
                theme: 'grid'
            });
            doc.save(`${fileName}.pdf`);
        }
        setActiveMenu(null);
    };

    const handleFind = (findText: string): boolean => {
        const lowerFind = findText.toLowerCase();
        const populatedCells = Array.from(cellsMap.entries()).map(([key, val]) => {
            const coords = parseCellId(key);
            return { key, r: coords.r, c: coords.c, val };
        }).sort((a, b) => {
            if (a.r !== b.r) return a.r - b.r;
            return a.c - b.c;
        });

        if (populatedCells.length === 0) return false;

        let startIndex = -1;
        if (activeCellId) {
            const exactIndex = populatedCells.findIndex(c => c.key === activeCellId);
            if (exactIndex !== -1) {
                startIndex = exactIndex;
            } else {
                const activeCoords = parseCellId(activeCellId);
                const nextIndex = populatedCells.findIndex(c => 
                    c.r > activeCoords.r || (c.r === activeCoords.r && c.c > activeCoords.c)
                );
                startIndex = nextIndex !== -1 ? nextIndex - 1 : populatedCells.length - 1;
            }
        }
        for (let i = startIndex + 1; i < populatedCells.length; i++) {
            if (populatedCells[i].val.toLowerCase().includes(lowerFind)) {
                const cell = populatedCells[i];
                setActiveCellId(cell.key);
                setSelection({ startC: cell.c, endC: cell.c, startR: cell.r, endR: cell.r });
                return true;
            }
        }
        for (let i = 0; i <= Math.min(startIndex, populatedCells.length - 1); i++) {
            if (populatedCells[i] && populatedCells[i].val.toLowerCase().includes(lowerFind)) {
                const cell = populatedCells[i];
                setActiveCellId(cell.key);
                setSelection({ startC: cell.c, endC: cell.c, startR: cell.r, endR: cell.r });
                return true;
            }
        }
        return false;
    };

    const handleReplace = (findText: string, replaceText: string) => {
        if (isReadOnly || !activeCellId) return handleFind(findText);
        const currentVal = cellsMap.get(activeCellId);
        if (currentVal && currentVal.toLowerCase().includes(findText.toLowerCase())) {
            cellsMap.set(activeCellId, currentVal.replace(new RegExp(findText, 'i'), replaceText));
            handleFind(findText);
        } else {
            handleFind(findText);
        }
    };

    const handleReplaceAll = (findText: string, replaceText: string) => {
        if (isReadOnly) return;
        const lowerFind = findText.toLowerCase();
        const regex = new RegExp(findText, 'gi');
        ydoc.transact(() => {
            for (const [key, val] of cellsMap.entries()) {
                if (val.toLowerCase().includes(lowerFind)) {
                    cellsMap.set(key, val.replace(regex, replaceText));
                }
            }
        });
    };

    const applyFormat = (key: string, value: any) => {
        if (isReadOnly || (!selection && !activeCellId)) return;
        ydoc.transact(() => {
            const cellsToFormat: string[] = [];
            if (selection) {
                const minR = Math.min(selection.startR, selection.endR);
                const maxR = Math.max(selection.startR, selection.endR);
                const minC = Math.min(selection.startC, selection.endC);
                const maxC = Math.max(selection.startC, selection.endC);
                const safeMaxR = Math.min(maxR, minR + 1000); 
                const safeMaxC = Math.min(maxC, minC + 100);
                for (let r = minR; r <= safeMaxR; r++) {
                    for (let c = minC; c <= safeMaxC; c++) {
                        cellsToFormat.push(toCoord(c, r));
                    }
                }
            } else if (activeCellId) {
                cellsToFormat.push(activeCellId);
            }
            cellsToFormat.forEach(cellId => {
                const currentFormat = formatsMap.get(cellId) || {};
                if (value === null) {
                    const newFormat = { ...currentFormat };
                    delete newFormat[key];
                    if (Object.keys(newFormat).length === 0) formatsMap.delete(cellId);
                    else formatsMap.set(cellId, newFormat);
                } else {
                    formatsMap.set(cellId, { ...currentFormat, [key]: value });
                }
            });
        });
    };

    const setCurrencyFormat = (code: string) => {
        if (isReadOnly || (!selection && !activeCellId)) return;
        ydoc.transact(() => {
            const cellsToFormat: string[] = [];
            if (selection) {
                const minR = Math.min(selection.startR, selection.endR);
                const maxR = Math.max(selection.startR, selection.endR);
                const minC = Math.min(selection.startC, selection.endC);
                const maxC = Math.max(selection.startC, selection.endC);
                const safeMaxR = Math.min(maxR, minR + 1000); 
                const safeMaxC = Math.min(maxC, minC + 100);
                for (let r = minR; r <= safeMaxR; r++) {
                    for (let c = minC; c <= safeMaxC; c++) {
                        cellsToFormat.push(toCoord(c, r));
                    }
                }
            } else if (activeCellId) {
                cellsToFormat.push(activeCellId);
            }
            cellsToFormat.forEach(cellId => {
                const currentFormat = formatsMap.get(cellId) || {};
                formatsMap.set(cellId, { ...currentFormat, numberFormat: 'currency', currencyCode: code });
            });
        });
        setActiveMenu(null);
    };

    const clearFormatting = (contextR?: number, contextC?: number) => {
        if (isReadOnly) return;
        ydoc.transact(() => {
            if (contextR !== undefined && contextC !== undefined) {
                formatsMap.delete(toCoord(contextC, contextR));
            } else if (selection) {
                const minR = Math.min(selection.startR, selection.endR);
                const maxR = Math.max(selection.startR, selection.endR);
                const minC = Math.min(selection.startC, selection.endC);
                const maxC = Math.max(selection.startC, selection.endC);
                for (const key of Array.from(formatsMap.keys())) {
                    const coords = parseCellId(key);
                    if (coords.r >= minR && coords.r <= maxR && coords.c >= minC && coords.c <= maxC) {
                        formatsMap.delete(key);
                    }
                }
            } else if (activeCellId) {
                formatsMap.delete(activeCellId);
            }
        });
    };

    const handleSaveConditionalRule = (condition: string, bg: string) => {
        if (isReadOnly || (!activeCellId && !selection)) return;
        let rangeStr = activeCellId as string;
        if (selection) {
            const minR = Math.min(selection.startR, selection.endR);
            const maxR = Math.max(selection.startR, selection.endR);
            const minC = Math.min(selection.startC, selection.endC);
            const maxC = Math.max(selection.startC, selection.endC);
            if (minR !== maxR || minC !== maxC) {
                rangeStr = `${toCoord(minC, minR)}:${toCoord(maxC, maxR)}`;
            } else {
                rangeStr = toCoord(minC, minR);
            }
        }
        ydoc.transact(() => {
            conditionalRules.push([{
                sheetId: activeSheetId,
                range: rangeStr,
                condition,
                format: { bg }
            }]);
        });
    };

    const handleDeleteConditionalRule = (index: number) => {
        if (isReadOnly) return;
        conditionalRules.delete(index, 1);
    };

    const currentRulesRaw = conditionalRules.toArray().map((r, i) => ({ ...r, originalIndex: i }));
    const activeRules = currentRulesRaw.filter(r => r.sheetId === activeSheetId);

    const getSortCol = () => {
        if (activeCellId) return parseCellId(activeCellId).c;
        if (selection) return Math.min(selection.startC, selection.endC);
        return 0; 
    };

    const handleSort = (isSheet: boolean, ascending: boolean) => {
        if (isReadOnly || (!activeCellId && !selection)) return;
        const sortCol = getSortCol();
        let minR = 0, maxR = 0, minC = 0, maxC = 0;
        if (isSheet) {
            let maxFoundR = -1;
            let maxFoundC = -1;
            for (const key of cellsMap.keys()) {
                const coords = parseCellId(key);
                if (coords.r > maxFoundR) maxFoundR = coords.r;
                if (coords.c > maxFoundC) maxFoundC = coords.c;
            }
            if (maxFoundR === -1) { setActiveMenu(null); return; }
            maxR = maxFoundR;
            maxC = maxFoundC;
        } else {
            if (!selection) { setActiveMenu(null); return; }
            minR = Math.min(selection.startR, selection.endR);
            maxR = Math.max(selection.startR, selection.endR);
            minC = Math.min(selection.startC, selection.endC);
            maxC = Math.max(selection.startC, selection.endC);
        }
        const rowsData: Array<{ r: number, sortVal: any, rowCells: Record<number, string>, rowFormats: Record<number, any> }> = [];
        for (let r = minR; r <= maxR; r++) {
            const sortKey = toCoord(sortCol, r);
            const sortVal = computedValues[sortKey] ?? cellsMap.get(sortKey) ?? '';
            const rowCells: Record<number, string> = {};
            const rowFormats: Record<number, any> = {};
            for (let c = minC; c <= maxC; c++) {
                const cellId = toCoord(c, r);
                const cellVal = cellsMap.get(cellId);
                const formatVal = formatsMap.get(cellId);
                if (cellVal !== undefined) rowCells[c] = cellVal;
                if (formatVal !== undefined) rowFormats[c] = formatVal;
            }
            rowsData.push({ r, sortVal, rowCells, rowFormats });
        }
        rowsData.sort((a, b) => {
            const valA = a.sortVal;
            const valB = b.sortVal;
            const isEmptyA = valA === '' || valA === null || valA === undefined;
            const isEmptyB = valB === '' || valB === null || valB === undefined;
            if (isEmptyA && isEmptyB) return 0;
            if (isEmptyA) return 1;
            if (isEmptyB) return -1;
            const numA = Number(valA);
            const numB = Number(valB);
            const isNumA = !isNaN(numA);
            const isNumB = !isNaN(numB);
            let cmp = 0;
            if (isNumA && isNumB) {
                cmp = numA - numB;
            } else {
                cmp = String(valA).localeCompare(String(valB));
            }
            return ascending ? cmp : -cmp;
        });
        ydoc.transact(() => {
            for (let r = minR; r <= maxR; r++) {
                for (let c = minC; c <= maxC; c++) {
                    const cellId = toCoord(c, r);
                    cellsMap.delete(cellId);
                    formatsMap.delete(cellId);
                }
            }
            rowsData.forEach((rowData, index) => {
                const newR = minR + index;
                for (let c = minC; c <= maxC; c++) {
                    const cellId = toCoord(c, newR);
                    if (rowData.rowCells[c] !== undefined) cellsMap.set(cellId, rowData.rowCells[c]);
                    if (rowData.rowFormats[c] !== undefined) formatsMap.set(cellId, rowData.rowFormats[c]);
                }
            });
        });
        setActiveMenu(null);
    };

    const handleApplyFilter = (colName: string, condition: string) => {
        const colIndex = colNameToIndex(colName);
        if (colIndex >= 0) {
            setFilterRule({ col: colIndex, condition });
        }
    };

    const handleSplitText = (delimiter: string) => {
        if (isReadOnly || (!selection && !activeCellId)) return;
        const targetC = selection ? Math.min(selection.startC, selection.endC) : parseCellId(activeCellId!).c;
        const startR = selection ? Math.min(selection.startR, selection.endR) : parseCellId(activeCellId!).r;
        const endR = selection ? Math.max(selection.startR, selection.endR) : parseCellId(activeCellId!).r;
        ydoc.transact(() => {
            for(let r = startR; r <= endR; r++) {
                const cellId = toCoord(targetC, r);
                const val = computedValues[cellId] ?? cellsMap.get(cellId) ?? '';
                if (typeof val === 'string' && val) {
                    const parts = val.split(delimiter);
                    parts.forEach((part, i) => {
                        if (part) cellsMap.set(toCoord(targetC + i, r), part.trim());
                    });
                }
            }
        });
        setActiveMenu(null);
    };

    const getActiveR = () => activeCellId ? parseCellId(activeCellId).r : 0;
    const getActiveC = () => activeCellId ? parseCellId(activeCellId).c : 0;
    const getRowLabel = () => {
        if (selection) {
            const start = Math.min(selection.startR, selection.endR) + 1;
            const end = Math.max(selection.startR, selection.endR) + 1;
            return start === end ? `Row ${start}` : `Rows ${start}-${end}`;
        }
        return `Row ${getActiveR() + 1}`;
    };
    const getColLabel = () => {
        if (selection) {
            const start = getColName(Math.min(selection.startC, selection.endC));
            const end = getColName(Math.max(selection.startC, selection.endC));
            return start === end ? `Column ${start}` : `Columns ${start}-${end}`;
        }
        return `Column ${getColName(getActiveC())}`;
    };

    // --- Helper Components for Responsive Menus ---
    const MenuItem = ({ label, onClick, disabled = false, isDestructive = false }: { label: string, onClick?: () => void, disabled?: boolean, isDestructive?: boolean }) => (
        <button 
            className={`block w-full text-left px-4 py-1.5 text-sm 
                ${disabled ? 'text-gray-400 cursor-not-allowed' : 
                  isDestructive ? 'text-red-600 hover:bg-red-50 hover:text-red-700' : 
                  'text-gray-700 hover:bg-gray-100 hover:text-gray-900'} transition-colors`}
            onClick={(e) => {
                e.stopPropagation();
                setActiveMenu(null); // Closes menu universally
                if (onClick && !disabled) onClick();
            }}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const DetailsMenu = ({ label, disabled = false, children }: { label: string, disabled?: boolean, children: React.ReactNode }) => (
        <details className="group/details" onClick={(e) => {
            e.stopPropagation();
            if (disabled) e.preventDefault();
        }}>
            <summary className={`list-none w-full text-left px-4 py-1.5 text-sm flex justify-between items-center select-none 
                ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100 cursor-pointer'} transition-colors`}>
                {label}
                <svg className={`w-4 h-4 transition-transform group-open/details:rotate-90 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
            </summary>
            {!disabled && (
                <div className="bg-gray-50 border-y border-gray-100 py-1 pl-2">
                    {children}
                </div>
            )}
        </details>
    );

    // --- Menu Definitions ---
    const fileMenuContent = (
        <>
            <MenuItem label="Share" disabled={isReadOnlyUser} onClick={onOpenShare} />
            <MenuItem label="Invite" disabled={isReadOnlyUser} onClick={onOpenShare} />
            <div className="border-t border-gray-200 my-1"></div>
            <DetailsMenu label="Import" disabled={isReadOnly}>
                <MenuItem label="Overwrite Document" isDestructive={true} onClick={() => triggerImport('overwrite-doc')} />
                <MenuItem label="Insert as New Sheets" onClick={() => triggerImport('insert-sheets')} />
                <MenuItem label="Replace Current Sheet" isDestructive={true} onClick={() => triggerImport('replace-sheet')} />
            </DetailsMenu>
            <DetailsMenu label="Export">
                <MenuItem label="Excel (.xlsx)" onClick={() => handleExport('xlsx')} />
                <MenuItem label="OpenDocument (.ods)" onClick={() => handleExport('ods')} />
                <MenuItem label="CSV (.csv)" onClick={() => handleExport('csv')} />
                <MenuItem label="TSV (.tsv)" onClick={() => handleExport('tsv')} />
                <MenuItem label="PDF (.pdf)" onClick={() => handleExport('pdf')} />
            </DetailsMenu>
            <MenuItem label="Print" onClick={handlePrint} />
            <div className="border-t border-gray-200 my-1"></div>
            <MenuItem label="Delete Document" isDestructive={true} disabled={isReadOnlyUser} onClick={() => setIsDeleteModalOpen(true)} />
            <MenuItem label="Close" onClick={() => navigate('/')} />
        </>
    );

    const editMenuContent = (
        <>
            <MenuItem label="Undo (Ctrl+Z)" disabled={isReadOnly} onClick={() => undoManager?.undo()} />
            <MenuItem label="Redo (Ctrl+Y)" disabled={isReadOnly} onClick={() => undoManager?.redo()} />
            <div className="border-t border-gray-200 my-1"></div>
            <MenuItem label="Cut" disabled={isReadOnly} onClick={() => handleCopy(true)} />
            <MenuItem label="Copy" onClick={() => handleCopy(false)} />
            <MenuItem label="Paste" disabled={isReadOnly} onClick={handlePaste} />
            <div className="border-t border-gray-200 my-1"></div>
            <MenuItem label="Find (Ctrl+F)" onClick={() => setIsFindReplaceModalOpen(true)} />
            <MenuItem label="Find & Replace" disabled={isReadOnly} onClick={() => setIsFindReplaceModalOpen(true)} />
            <div className="border-t border-gray-200 my-1"></div>
            <DetailsMenu label="Delete" disabled={isReadOnly}>
                <MenuItem label="Values" onClick={() => handleDeleteOperation('values')} />
                <MenuItem label="Formatting" onClick={() => handleDeleteOperation('formatting')} />
                <div className="border-t border-gray-200 my-1"></div>
                <MenuItem label={getRowLabel()} onClick={() => handleDeleteOperation('row')} />
                <MenuItem label={getColLabel()} onClick={() => handleDeleteOperation('col')} />
                <div className="border-t border-gray-200 my-1"></div>
                <MenuItem label="Cells & Shift Up" onClick={() => handleDeleteOperation('shiftUp')} />
                <MenuItem label="Cells & Shift Left" onClick={() => handleDeleteOperation('shiftLeft')} />
            </DetailsMenu>
        </>
    );

    const viewMenuContent = (
        <>
            <DetailsMenu label="Freeze">
                <div className="px-4 py-1 text-xs font-bold text-gray-400">Rows</div>
                <MenuItem label="No Rows" onClick={() => setFreezeRow(0)} />
                <MenuItem label="1 Row" onClick={() => setFreezeRow(1)} />
                <MenuItem label="2 Rows" onClick={() => setFreezeRow(2)} />
                <MenuItem label={`Up to Row ${getActiveR() + 1}`} onClick={() => setFreezeRow(getActiveR() + 1)} />
                <div className="border-t border-gray-200 my-1"></div>
                <div className="px-4 py-1 text-xs font-bold text-gray-400">Columns</div>
                <MenuItem label="No Columns" onClick={() => setFreezeCol(0)} />
                <MenuItem label="1 Column" onClick={() => setFreezeCol(1)} />
                <MenuItem label="2 Columns" onClick={() => setFreezeCol(2)} />
                <MenuItem label={`Up to Column ${getColName(getActiveC())}`} onClick={() => setFreezeCol(getActiveC() + 1)} />
            </DetailsMenu>
            <DetailsMenu label="Group">
                <MenuItem label="Group Rows" onClick={() => handleGroup('row', true)} />
                <MenuItem label="Ungroup Rows" onClick={() => handleGroup('row', false)} />
                <MenuItem label="Group Columns" onClick={() => handleGroup('col', true)} />
                <MenuItem label="Ungroup Columns" onClick={() => handleGroup('col', false)} />
            </DetailsMenu>
            <div className="border-t border-gray-200 my-1"></div>
            <DetailsMenu label={`Zoom (${Math.round(zoom * 100)}%)`}>
                <div className="max-h-48 overflow-y-auto">
                    {Array.from({length: 23}, (_, i) => 30 + i * 10).map(z => (
                        <MenuItem key={z} label={`${z}%`} onClick={() => setZoom(z / 100)} />
                    ))}
                </div>
            </DetailsMenu>
        </>
    );

    const insertMenuContent = (
        <>
            <MenuItem label="Row above" disabled={isReadOnly} onClick={() => { 
                const r = getTargetRow();
                if (r !== null) shiftCells('row', r); 
            }} />
            <MenuItem label="Column left" disabled={isReadOnly} onClick={() => { 
                const c = getTargetCol();
                if (c !== null) shiftCells('col', c); 
            }} />
            <div className="border-t border-gray-200 my-1"></div>
            <MenuItem label="Sheet" disabled={isReadOnly} onClick={() => {
                const newId = `sheet-${Date.now()}`;
                ydoc.transact(() => {
                    sheetOrder.push([newId]);
                    sheetMeta.set(newId, `Sheet${sheetOrder.length}`);
                });
                setActiveSheetId(newId);
                setActiveCellId(null);
                setSelection(null);
            }} />
        </>
    );

    const formatMenuContent = (
        <>
            <MenuItem label="Bold" disabled={isReadOnly} onClick={() => applyFormat('bold', formatsMap.get(activeCellId || '')?.bold ? null : true)} />
            <MenuItem label="Italic" disabled={isReadOnly} onClick={() => applyFormat('italic', formatsMap.get(activeCellId || '')?.italic ? null : true)} />
            <DetailsMenu label="Font Family" disabled={isReadOnly}>
                <div className="max-h-48 overflow-y-auto">
                    {FONTS.map(font => (
                        <div key={font} style={{ fontFamily: font }}>
                            <MenuItem label={font} onClick={() => applyFormat('fontFamily', font)} />
                        </div>
                    ))}
                    <div className="border-t border-gray-200 my-1"></div>
                    <MenuItem label="Default" onClick={() => applyFormat('fontFamily', null)} />
                </div>
            </DetailsMenu>
            <div className="border-t border-gray-200 my-1"></div>
            <label className={`flex w-full items-center justify-between px-4 py-1.5 text-sm ${isReadOnly ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100 cursor-pointer'}`} onClick={e => e.stopPropagation()}>
                Text Color
                <input type="color" disabled={isReadOnly} onChange={(e) => applyFormat('color', e.target.value)} className="w-5 h-5 p-0 border-0 rounded cursor-pointer" defaultValue="#111827" />
            </label>
            <label className={`flex w-full items-center justify-between px-4 py-1.5 text-sm ${isReadOnly ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100 cursor-pointer'}`} onClick={e => e.stopPropagation()}>
                Fill Color
                <input type="color" disabled={isReadOnly} onChange={(e) => applyFormat('bg', e.target.value)} className="w-5 h-5 p-0 border-0 rounded cursor-pointer" defaultValue="#ffffff" />
            </label>
            <MenuItem label="Clear Color" disabled={isReadOnly} onClick={() => { applyFormat('bg', null); applyFormat('color', null); }} />
            <div className="border-t border-gray-200 my-1"></div>
            <DetailsMenu label="Currency" disabled={isReadOnly}>
                <MenuItem label="CAD" onClick={() => setCurrencyFormat('CAD')} />
                <MenuItem label="USD" onClick={() => setCurrencyFormat('USD')} />
                <MenuItem label="EUR" onClick={() => setCurrencyFormat('EUR')} />
                <MenuItem label="GBP" onClick={() => setCurrencyFormat('GBP')} />
                <div className="border-t border-gray-200 my-1"></div>
                <MenuItem label="Custom..." onClick={() => { setIsCurrencyModalOpen(true); setActiveMenu(null); }} />
            </DetailsMenu>
            <MenuItem label="Percentage" disabled={isReadOnly} onClick={() => applyFormat('numberFormat', 'percentage')} />
            <MenuItem label="Plain Text" disabled={isReadOnly} onClick={() => applyFormat('numberFormat', null)} />
            <div className="border-t border-gray-200 my-1"></div>
            <MenuItem label="Conditional Formatting" disabled={isReadOnly} onClick={() => { setIsConditionalModalOpen(true); setActiveMenu(null); }} />
            <div className="border-t border-gray-200 my-1"></div>
            <MenuItem label="Clear Formatting" disabled={isReadOnly} onClick={() => clearFormatting()} />
        </>
    );

    const dataMenuContent = (
        <>
            <MenuItem label={`Sort Range by ${getColLabel()} (A-Z)`} disabled={isReadOnly} onClick={() => handleSort(false, true)} />
            <MenuItem label={`Sort Range by ${getColLabel()} (Z-A)`} disabled={isReadOnly} onClick={() => handleSort(false, false)} />
            <div className="border-t border-gray-200 my-1"></div>
            <MenuItem label={`Sort Sheet by ${getColLabel()} (A-Z)`} disabled={isReadOnly} onClick={() => handleSort(true, true)} />
            <MenuItem label={`Sort Sheet by ${getColLabel()} (Z-A)`} disabled={isReadOnly} onClick={() => handleSort(true, false)} />
            <div className="border-t border-gray-200 my-1"></div>
            <MenuItem label="Filter" onClick={() => { setIsFilterModalOpen(true); setActiveMenu(null); }} />
            {filterRule && <MenuItem label="Clear Filter" onClick={() => setFilterRule(null)} />}
            <div className="border-t border-gray-200 my-1"></div>
            <MenuItem label="Split Text to Columns" disabled={isReadOnly} onClick={() => { setIsSplitTextModalOpen(true); setActiveMenu(null); }} />
        </>
    );

    const helpMenuContent = (
        <>
            <MenuItem label="Formula List" onClick={() => { setIsFormulaListModalOpen(true); setActiveMenu(null); }} />
            <div className="border-t border-gray-200 my-1"></div>
            <MenuItem label="Feedback" onClick={() => { setIsFeedbackModalOpen(true); setActiveMenu(null); }} />
        </>
    );

    const menusList = [
        { id: 'File', label: t('menu.file'), content: fileMenuContent },
        { id: 'Edit', label: t('menu.edit'), content: editMenuContent },
        { id: 'View', label: t('menu.view'), content: viewMenuContent },
        { id: 'Insert', label: t('menu.insert'), content: insertMenuContent },
        { id: 'Formatting', label: t('menu.formatting'), content: formatMenuContent },
        { id: 'Data', label: t('menu.data'), content: dataMenuContent },
        { id: 'Help', label: t('menu.help'), content: helpMenuContent },
    ];

    return (
        <div className="flex flex-col h-full w-full bg-white shadow-lg overflow-hidden">
            {isOverQuota && (
                <div className="bg-red-600 text-white px-4 py-2 text-center text-sm font-bold shadow-md z-50 shrink-0 flex justify-center items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                    STORAGE QUOTA EXCEEDED - READ ONLY MODE. PLEASE DELETE SPREADSHEETS TO RESUME EDITING.
                </div>
            )}
            <div className="bg-gray-50 border-b border-gray-200 px-2 py-1 flex items-center space-x-1 text-sm text-gray-700 shrink-0 z-50 relative">
                
                {/* Mobile Menu Button */}
                <div className="md:hidden relative">
                    <button 
                        onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === 'MobileMenu' ? null : 'MobileMenu'); }} 
                        className={`flex items-center px-3 py-1.5 rounded transition-colors font-bold ${activeMenu === 'MobileMenu' ? 'bg-gray-200 text-gray-900' : 'hover:bg-gray-200'}`}
                    >
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
                        {t('menu.menu', 'Menu')}
                    </button>
                    {activeMenu === 'MobileMenu' && (
                        <div 
                            className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 shadow-xl rounded py-2 z-50 max-h-[70vh] overflow-y-auto custom-scrollbar"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {menusList.map(menu => (
                                <DetailsMenu key={menu.id} label={menu.label}>
                                    {menu.content}
                                </DetailsMenu>
                            ))}
                            {isInstallable && (
                                <>
                                    <div className="border-t border-gray-200 my-1"></div>
                                    <MenuItem 
                                        label={t('nav.installApp', 'Install App')} 
                                        onClick={async () => {
                                            const res = await promptInstall();
                                            if (res === 'ios_instructions') alert(t('install.iosInstructions'));
                                        }} 
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center space-x-1">
                    {menusList.map(menu => (
                        <div key={menu.id} className="relative" onMouseEnter={() => handleMenuHover(menu.id)}>
                            <button onClick={(e) => handleMenuClick(e, menu.id)} className={`px-2 py-1 rounded transition-colors ${activeMenu === menu.id ? 'bg-gray-200 text-gray-900' : 'hover:bg-gray-200'}`}>
                                {menu.label}
                            </button>
                            {activeMenu === menu.id && (
                                <div 
                                    className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 shadow-xl rounded py-1 max-h-[80vh] overflow-y-auto custom-scrollbar"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {menu.content}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                
                {filterRule && (
                    <div className="ml-auto mr-4 flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded text-xs font-bold border border-blue-200">
                        <svg className="w-3 h-3 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
                        Filtering Col {getColName(filterRule.col)}: {filterRule.condition}
                        <button onClick={() => setFilterRule(null)} className="ml-3 text-blue-400 hover:text-blue-800">✕</button>
                    </div>
                )}
            </div>

            <FormulaBar activeCell={activeCellId} cellsMap={cellsMap} computedValue={currentComputedValue} isReadOnly={isReadOnly} />
            
            <div className="flex-1 relative overflow-hidden bg-white">
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="hidden" 
                    accept=".xlsx,.ods,.csv,.tsv" 
                    onChange={handleFileSelected} 
                />
                <VirtualisedGrid 
                    key={activeSheetId}
                    ydoc={ydoc} 
                    cellsMap={cellsMap}
                    formatsMap={formatsMap}
                    colWidthsMap={colWidthsMap}
                    rowHeightsMap={rowHeightsMap}
                    conditionalRules={conditionalRules}
                    activeSheetId={activeSheetId}
                    computedValues={computedValues}
                    awareness={awareness} 
                    activeCellId={activeCellId}
                    onCellSelect={setActiveCellId} 
                    selection={selection}
                    setSelection={setSelection}
                    onInsertRow={(r, count) => shiftCells('row', r, count)}
                    onInsertCol={(c, count) => shiftCells('col', c, count)}
                    onClearFormat={clearFormatting}
                    hiddenRows={effectiveHiddenRows}
                    groupedRows={groupedRows}
                    groupedCols={groupedCols}
                    onUngroupRow={handleUngroupRowBlock}
                    onUngroupCol={handleUngroupColBlock}
                    zoom={zoom}
                    freezeRow={freezeRow}
                    freezeCol={freezeCol}
                    totalRows={totalRows}
                    totalCols={totalCols}
                    isReadOnly={isReadOnly}
                />
            </div>

            <div className="flex bg-gray-100 border-t border-gray-300 overflow-x-auto shrink-0 select-none items-end z-20">
                {sheetOrder.toArray().map((sheetId) => (
                    <div 
                        key={sheetId}
                        onClick={() => {
                            if (activeSheetId !== sheetId) {
                                setActiveSheetId(sheetId);
                                setActiveCellId(null);
                                setSelection(null);
                            }
                        }}
                        className={`px-4 py-2 border-r border-gray-300 cursor-pointer text-sm min-w-[100px] text-center flex items-center justify-between group
                            ${activeSheetId === sheetId 
                                ? 'bg-white text-green-700 font-bold border-t-2 border-t-green-600' 
                                : 'bg-gray-200 text-gray-600 font-medium hover:bg-gray-50'}`}
                    >
                        {editingSheet === sheetId && !isReadOnly ? (
                            <input 
                                autoFocus
                                defaultValue={sheetMeta.get(sheetId)}
                                onBlur={(e) => {
                                    if (e.target.value.trim()) sheetMeta.set(sheetId, e.target.value.trim());
                                    setEditingSheet(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.currentTarget.blur();
                                }}
                                className="outline-none border-none bg-transparent px-1 w-full text-center text-sm font-bold text-green-700"
                            />
                        ) : (
                            <>
                                <span onDoubleClick={() => { if(!isReadOnly) setEditingSheet(sheetId); }} className="block w-full truncate">
                                    {sheetMeta.get(sheetId) || 'Unnamed'}
                                </span>
                                {!isReadOnly && sheetOrder.length > 1 && (
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleDeleteSheet(sheetId); }}
                                        className={`ml-2 px-1 text-gray-400 hover:text-red-500 rounded hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity ${activeSheetId === sheetId ? 'opacity-100' : ''}`}
                                        title="Delete Sheet"
                                    >
                                        ✕
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                ))}
                
                <button 
                    onClick={() => {
                        if (isReadOnly) return;
                        const newId = `sheet-${Date.now()}`;
                        ydoc.transact(() => {
                            sheetOrder.push([newId]);
                            sheetMeta.set(newId, `Sheet${sheetOrder.length}`);
                        });
                        setActiveSheetId(newId);
                        setActiveCellId(null);
                        setSelection(null);
                    }}
                    disabled={isReadOnly}
                    className={`px-4 py-2 font-bold text-lg ${isReadOnly ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-green-600 hover:bg-gray-200'}`}
                >
                    +
                </button>
            </div>

            <ConditionalFormatModal
                isOpen={isConditionalModalOpen}
                activeRules={activeRules}
                onClose={() => setIsConditionalModalOpen(false)}
                onSave={handleSaveConditionalRule}
                onDelete={handleDeleteConditionalRule}
            />

            <FindReplaceModal
                isOpen={isFindReplaceModalOpen}
                onClose={() => setIsFindReplaceModalOpen(false)}
                onFind={handleFind}
                onReplace={handleReplace}
                onReplaceAll={handleReplaceAll}
            />

            <CustomCurrencyModal
                isOpen={isCurrencyModalOpen}
                onClose={() => setIsCurrencyModalOpen(false)}
                onSave={setCurrencyFormat}
            />

            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                onApply={handleApplyFilter}
            />

            <SplitTextModal
                isOpen={isSplitTextModalOpen}
                onClose={() => setIsSplitTextModalOpen(false)}
                onApply={handleSplitText}
            />

            <FormulaListModal
                isOpen={isFormulaListModalOpen}
                onClose={() => setIsFormulaListModalOpen(false)}
            />

            <FeedbackModal
                isOpen={isFeedbackModalOpen}
                onClose={() => setIsFeedbackModalOpen(false)}
            />

            <ConfirmModal 
                isOpen={isDeleteModalOpen}
                title="Delete Document"
                message="Are you sure you want to delete this spreadsheet? This action is permanent and cannot be reversed."
                confirmText="Delete Permanently"
                isDestructive={true}
                onConfirm={confirmDeleteDocument}
                onCancel={() => setIsDeleteModalOpen(false)}
            />
        </div>
    );
};